/**
 * AudioManager - Centralized audio playback manager
 * Prevents audio overlap using a 3-level priority system:
 * - Priority 1 (Highest): WELCOME_TEXT - blocks all other audio
 * - Priority 2 (Medium): Movement instructions - blocks level 3
 * - Priority 3 (Lowest): Real-time feedback - can be interrupted by 1 & 2
 */
class AudioManager {
    constructor() {
        this.isPlaying = false;
        this.currentAudioUrl = null;
        this.currentPriorityLevel = 0; // 0 = no audio, 1 = highest, 2 = medium, 3 = lowest
    }

    /**
     * Check if any audio is currently playing
     * @returns {boolean} True if audio is playing, false otherwise
     */
    getIsPlaying() {
        return this.isPlaying;
    }

    /**
     * Get the current audio priority level
     * @returns {number} Priority level (0 = no audio, 1 = highest, 2 = medium, 3 = lowest)
     */
    getCurrentPriorityLevel() {
        return this.currentPriorityLevel;
    }

    /**
     * Check if priority audio is currently playing (for backward compatibility)
     * @returns {boolean} True if priority level 1 or 2 is playing
     * @deprecated Use getCurrentPriorityLevel() for more granular control
     */
    getIsPriorityAudioPlaying() {
        return this.currentPriorityLevel > 0 && this.currentPriorityLevel <= 2;
    }

    /**
     * Get the URL of currently playing audio
     * @returns {string|null} URL of current audio or null
     */
    getCurrentAudioUrl() {
        return this.currentAudioUrl;
    }

    /**
     * Set the playing state with priority level
     * @param {boolean} playing - Whether audio is playing
     * @param {string|null} url - URL of the audio (optional)
     * @param {number} priorityLevel - Priority level: 1 (highest), 2 (medium), 3 (lowest)
     */
    setPlaying(playing, url = null, priorityLevel = 3) {
        this.isPlaying = playing;
        this.currentAudioUrl = playing ? url : null;
        this.currentPriorityLevel = playing ? priorityLevel : 0;
    }

    /**
     * Reset the audio state (used when stopping audio)
     */
    reset() {
        this.isPlaying = false;
        this.currentAudioUrl = null;
        this.currentPriorityLevel = 0;
    }
}

// Export singleton instance
const audioManager = new AudioManager();
export default audioManager;
